import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, fmtINR } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash, CaretLeft, CaretRight, HandCoins } from "@phosphor-icons/react";

const MONTHS_SHOWN = 6;
const ym = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
const monthLabel = (m) => new Date(`${m}-01T00:00:00`).toLocaleDateString("en-IN", { month: "short", year: "2-digit" });

function monthsFrom(offset) {
  const now = new Date();
  return Array.from({ length: MONTHS_SHOWN }, (_, i) => ym(new Date(now.getFullYear(), now.getMonth() - 2 + offset + i, 1)));
}

const TONE = {
  received: "text-emerald-600",
  partial: "text-amber-600",
  due: "text-rose-600",
};

function DueCell({ src, month, due, canEdit, onSave }) {
  const [val, setVal] = useState(due ? String(due.amount) : "");
  useEffect(() => { setVal(due ? String(due.amount) : ""); }, [due]);
  const commit = () => {
    const prev = due ? String(due.amount) : "";
    if (val.trim() === prev) return;
    onSave(src.id, month, val.trim() === "" ? 0 : Number(val));
  };
  const tip = due?.credits?.length
    ? due.credits.map((c) => `${c.date} · ${fmtINR(c.amount)} · ${c.account || ""}`).join("\n")
    : "";
  return (
    <td className="border border-slate-200 p-0 align-top min-w-[118px]" title={tip}>
      <input
        type="number" min="0" step="0.01" value={val} disabled={!canEdit}
        onChange={(e) => setVal(e.target.value)} onBlur={commit}
        onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
        placeholder={canEdit ? "—" : ""}
        className="w-full h-8 px-2 text-right font-mono tabular-nums text-[12px] bg-transparent outline-none focus:ring-2 focus:ring-inset focus:ring-cyan-500 disabled:cursor-default"
        data-testid={`oi-cell-${src.id}-${month}`}
      />
      {due && (
        <div className={`px-2 pb-1 text-right text-[9.5px] font-mono ${TONE[due.status]}`} data-testid={`oi-status-${src.id}-${month}`}>
          {due.status === "received" ? "Received" : due.status === "partial" ? `Due ${fmtINR(due.remaining)}` : "Due"}
        </div>
      )}
    </td>
  );
}

export default function OtherIncomeSection({ refreshKey, onChanged }) {
  const [data, setData] = useState(null);
  const [offset, setOffset] = useState(0);
  const [newName, setNewName] = useState("");
  const months = useMemo(() => monthsFrom(offset), [offset]);

  const load = useCallback(() => {
    api.get("/other-income").then((r) => setData(r.data)).catch(() => setData({ sources: [], total_remaining: 0 }));
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const after = () => { load(); onChanged?.(); };
  const addSource = async () => {
    if (!newName.trim()) return;
    try {
      await api.post("/other-income/sources", { name: newName.trim() });
      setNewName("");
      toast.success("Party added");
      after();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add party"); }
  };
  const saveDue = async (source_id, month, amount) => {
    try {
      await api.put("/other-income/dues", { source_id, month, amount });
      after();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
  };
  const removeSource = async (src) => {
    if (!window.confirm(`Remove ${src.name} and all its monthly dues?`)) return;
    try { await api.delete(`/other-income/sources/${src.id}`); after(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not remove"); }
  };

  if (!data) return null;
  const canEdit = !!data.can_edit;

  return (
    <div className="rounded-md bg-white border border-slate-200 p-5 shadow-sm" data-testid="other-income-section">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-2">
          <HandCoins size={18} weight="duotone" className="text-emerald-600" />
          <div className="font-heading font-bold text-slate-900">Other income dues</div>
          <span className="text-[11px] text-slate-500">Auto-marked received when the party's name shows up in a bank credit</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[11px] text-slate-500">Still due: <b className="font-mono text-emerald-700" data-testid="oi-total-remaining">{fmtINR(data.total_remaining)}</b></span>
          <button type="button" onClick={() => setOffset((o) => o - 1)} className="h-7 w-7 grid place-items-center rounded-sm border border-slate-200 hover:bg-slate-100" data-testid="oi-prev-months"><CaretLeft size={12} weight="bold" /></button>
          <button type="button" onClick={() => setOffset((o) => o + 1)} className="h-7 w-7 grid place-items-center rounded-sm border border-slate-200 hover:bg-slate-100" data-testid="oi-next-months"><CaretRight size={12} weight="bold" /></button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="border-collapse text-[12px] min-w-full">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-slate-500">
              <th className="border border-slate-200 px-3 py-2 text-left min-w-[220px]">Party (as in bank statement)</th>
              {months.map((m) => <th key={m} className="border border-slate-200 px-2 py-2 text-right">{monthLabel(m)}</th>)}
              <th className="border border-slate-200 px-2 py-2 text-right">Still due</th>
              {canEdit && <th className="border border-slate-200 w-8" />}
            </tr>
          </thead>
          <tbody>
            {data.sources.length === 0 && (
              <tr><td colSpan={months.length + 3} className="py-6 text-center text-slate-500 border border-slate-200">No income sources yet{canEdit ? ". Add a party below" : ""}.</td></tr>
            )}
            {data.sources.map((src) => {
              const byMonth = Object.fromEntries(src.dues.map((d) => [d.month, d]));
              return (
                <tr key={src.id} data-testid={`oi-row-${src.id}`}>
                  <td className="border border-slate-200 px-3 py-1.5 font-semibold text-slate-800">{src.name}</td>
                  {months.map((m) => <DueCell key={m} src={src} month={m} due={byMonth[m]} canEdit={canEdit} onSave={saveDue} />)}
                  <td className="border border-slate-200 px-2 text-right font-mono font-bold text-slate-800" data-testid={`oi-remaining-${src.id}`}>{fmtINR(src.total_remaining)}</td>
                  {canEdit && (
                    <td className="border border-slate-200 text-center">
                      <button type="button" onClick={() => removeSource(src)} className="text-slate-400 hover:text-rose-500" data-testid={`oi-delete-${src.id}`}><Trash size={13} /></button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {canEdit && (
        <div className="mt-3 flex items-center gap-2">
          <input value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") addSource(); }}
            placeholder="Party name exactly as in bank statement"
            className="h-8 w-80 px-2 text-[12px] rounded-sm border border-slate-200 bg-transparent outline-none focus:ring-2 focus:ring-cyan-500"
            data-testid="oi-new-party-input" />
          <button type="button" onClick={addSource}
            className="h-8 px-3 rounded-sm bg-emerald-600 hover:bg-emerald-500 text-white text-[12px] font-semibold flex items-center gap-1"
            data-testid="oi-add-party-btn">
            <Plus size={12} weight="bold" />Add party
          </button>
        </div>
      )}
    </div>
  );
}
